import type { UIAlertTypeUnion } from '@core/types'
import type { AuthAlertAction } from './useAuthContainer'

// Nothing guarantees a result ever comes back: an app build that ignores start_mit_id, a
// postMessage that goes nowhere, an external navigation a webview refuses. This is wall clock
// and it runs while the native sheet is up, so it has to outlast a worst-case interaction
// there: reading the request, a PIN or biometrics, and a MitID approval that can bounce
// through another app, on a phone network. Two minutes covers that; a successful flow leaves
// this document long before it.
const PENDING_WATCHDOG_MS = 120_000

// A page load is reported once, though the signup dialog sets this composable up on every open.
// Back/forward cache restores do not check it: each one ends a flow of its own and has a loader to clear.
let backReported = false

// The user backing out of MobilePay (web redirect, app sheet) is no failure.
const CANCELLED_ERRORS = ['access_denied', 'dismissed_or_cancelled']

export interface LoginWithMobilePayOptions {
    signup: boolean
    promotionId?: string
    promotionAccepted?: boolean
}

export type MobilePayPromotionPick = Pick<LoginWithMobilePayOptions, 'promotionId' | 'promotionAccepted'>

// The signup's bonus pick, kept across the full page load of the MobilePay round trip.
export const useMobilePayPromotionPick = () => useCookie<MobilePayPromotionPick | undefined>('mobilepay_signup_promotion', {
    secure: true,
    maxAge: 30 * 60,
})

export const useLoginWithMobilePay = () => {
    const { site } = useCoreConfig()
    const { isMobileApp, pendingEidSessionId } = useMobileApp()
    const { t } = useI18n()
    const safeRedirect = useSafeRedirect()
    const userInfo = useUserInfo()
    const route = useRoute()
    const { trackEvent } = useTracking()
    const signupVersion = useSignupVersion()
    const { startMobilePay, exchangeMobilePay, mobilePayError } = useUserAuthClient()
    const links = useLinks()

    const mobilePaySignupCookie = useCookie<boolean | undefined>('mobilepay_signup', {
        secure: true,
        maxAge: 30 * 60,
    })
    const promotionPick = useMobilePayPromotionPick()

    const errorMessage = t('core.auth.mobilepay.errors.generic')
    const genericExchangeErrorMessage = t('core.auth.mobilepay.errors.genericExchange')
    const timeoutMessage = t('core.auth.mobilepay.errors.timeout')

    const errorCodeMappings: Record<string, string> = {
        'UA:1100': t('core.auth.mobilepay.errors.UA1100'),
        'UA:1200': t('core.auth.mobilepay.errors.UA1200', { siteName: site.name }),
        'UA:1300': t('core.auth.mobilepay.errors.UA1300'),
        'UA:1700': t('core.auth.mobilepay.errors.UA1700'),
        'UA:2200': t('core.auth.mobilepay.errors.UA2200'),
    }

    // Why a MobilePay signup was refused (UA:2200); an unknown or missing reason keeps the UA:2200 text.
    const accountClosed = {
        message: t('core.auth.mobilepay.errors.accountClosed'),
        actions: [{ label: t('core.auth.mobilepay.actions.contactSupport'), onClick: () => links.support.navigate() }],
    }
    const contactInUse = {
        message: t('core.auth.mobilepay.errors.contactInUse'),
        actions: [{ label: t('core.auth.mobilepay.actions.loginWithEmail'), onClick: () => links.login.navigate() }],
    }
    const signupRejections: Record<string, { message: string, actions?: AuthAlertAction[] }> = {
        account_blocked: accountClosed,
        account_suspended: accountClosed,
        phone_in_use: contactInUse,
        email_in_use: contactInUse,
        gambling_authority_excluded: { message: t('core.auth.mobilepay.errors.UA1300') },
        underage: { message: t('core.auth.mobilepay.errors.underage') },
        invalid_ssn: { message: t('core.auth.mobilepay.errors.invalidSsn') },
    }

    // Old app builds may never deliver the eid bridge result, leaving pending stuck - clear it on
    // refocus, but only after login() armed it and the tab was hidden at least once. Registered
    // synchronously here: useEventListener has no Nuxt app context past an await inside login().
    let fallbackArmed = false
    let wasHidden = false

    // Both ways out of pending report through the setters login() was handed.
    let fallbackSetPending: ((pending: boolean) => void) | undefined
    let fallbackSetAlert: ((alert: { type: UIAlertTypeUnion, message: string }) => void) | undefined

    let watchdog: ReturnType<typeof setTimeout> | undefined

    const clearWatchdog = () => clearTimeout(watchdog)

    // A bare timer outlives the component that armed it. Left running, it would write a timeout
    // warning into the shared alert long after the user closed the dialog and moved on. Pending
    // needs no rescue here - AuthContainer resets it when the dialog next opens.
    tryOnScopeDispose(clearWatchdog)

    const triggerFallback = () => {
        if (!fallbackArmed || !wasHidden) return

        fallbackArmed = false
        clearWatchdog()

        // The app's pending signal can land on either side of this delay; once it has, a result
        // is still coming and the loader stays.
        setTimeout(() => {
            if (!pendingEidSessionId.value) fallbackSetPending?.(false)
        }, 1_500)
    }

    // The wasHidden fast lane above is not guaranteed to fire: a webview merely covered by a
    // native auth sheet is never hidden. This is the only unconditional way out of pending.
    const armWatchdog = () => {
        clearWatchdog()

        watchdog = setTimeout(() => {
            // fallbackArmed is left alone: the flow may still be live behind the sheet, and the
            // refocus fast lane is the one thing that would notice the user coming back.
            pendingEidSessionId.value = undefined
            fallbackSetPending?.(false)
            fallbackSetAlert?.({ type: 'warning', message: timeoutMessage })
        }, PENDING_WATCHDOG_MS)
    }

    // The refocus fast lane may already have cleared pending and dropped the watchdog by the time
    // the app reports the flow as still pending: put the loader back and leave the watchdog as
    // the only way out short of the result itself.
    watch(pendingEidSessionId, (sessionId) => {
        if (!sessionId || !fallbackSetPending) return

        fallbackArmed = false
        fallbackSetPending(true)
        armWatchdog()
    })

    // Set when the web flow leaves for the MobilePay page. A page the browser restores from the
    // back/forward cache comes back as it left: loader up, watchdog still counting.
    let leftForMobilePay = false

    // The flow never reached /eid/callback: the user came back from the MobilePay page.
    // Clearing the cookie ends the flow, so later back loads do not count the same click again.
    const reportBack = (navigation: 'bfcache' | 'back_forward') => {
        backReported = true

        const signup = mobilePaySignupCookie.value === true
        mobilePaySignupCookie.value = undefined

        trackEvent({
            event_name: signup ? 'signup_mobilepay_back' : 'login_mobilepay_back',
            event_type: 'Click',
            meta_data: [{ category: signup ? 'Signup' : 'Login', navigation }],
        })
    }

    // The composable also runs during SSR setup, where document/window don't exist.
    if (import.meta.client) {
        useEventListener(document, 'visibilitychange', () => {
            if (document.hidden) {
                wasHidden = true
                return
            }

            triggerFallback()
        })
        useEventListener(window, 'focus', triggerFallback)

        useEventListener(window, 'pageshow', (event: PageTransitionEvent) => {
            if (!event.persisted || !leftForMobilePay) return

            leftForMobilePay = false
            clearWatchdog()
            fallbackSetPending?.(false)
            reportBack('bfcache')
        })

        // The cookie lives until the callback, a failed start or a reported return clears it, so a
        // back navigation that loads a page with it still set left the flow on the MobilePay page.
        const [navigation] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]

        if (!backReported && typeof mobilePaySignupCookie.value === 'boolean' && navigation?.type === 'back_forward') {
            reportBack('back_forward')
        }
    }

    async function login(
        setAlert: (alert: { type: UIAlertTypeUnion, message: string }) => void,
        setPending: (pending: boolean) => void,
        { signup, promotionId, promotionAccepted }: LoginWithMobilePayOptions
    ) {
        // A new start ends any earlier session. The app drops a superseded session's dismiss, so its
        // id would never be cleared and the refocus check would keep the loader up for this one.
        pendingEidSessionId.value = undefined

        setPending(true)

        fallbackSetPending = setPending
        fallbackSetAlert = setAlert

        await trackEvent({
            event_name: signup ? 'signup_mobilepay' : 'login_mobilepay',
            event_type: 'Click',
            meta_data: [
                { category: signup ? 'Signup' : 'Login' },
            ],
        })

        mobilePaySignupCookie.value = signup
        promotionPick.value = signup ? { promotionId, promotionAccepted } : undefined

        try {
            if (route.query.returnUrl) {
                userInfo.returnUrlAfterLogin.value = route.query.returnUrl as string
            }
            else if (route.name !== 'index' && route.name !== 'konto-eid') {
                userInfo.returnUrlAfterLogin.value = route.path
            }

            const response = await startMobilePay({
                signup,
                promotionId,
                promotionAccepted,
            })

            if (!response) {
                throw new Error('MobilePay start returned no redirect_to')
            }

            // Neither path is guaranteed to come back: the app may deliver no bridge result at
            // all, and web has no bridge result to begin with — only the external navigation
            // clears pending there, and a blocked one leaves the loader up for good.
            armWatchdog()

            if (isMobileApp) {
                // Field apps only understand start_mit_id - flip to start_eid once min app version allows.
                // No provider field: the iOS app auto-dismisses the in-app session on an app-switch
                // return when provider is 'mobilepay', killing the still-live Vipps session.
                // flow is a separate field so the Android app can keep the MobilePay session alive without provider.
                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'start_mit_id', url: response.redirect_to, redirectUrl: 'spilnu://', flow: 'mobilepay' }))

                wasHidden = false
                fallbackArmed = true

                return
            }

            leftForMobilePay = true

            await navigateTo(response.redirect_to, { external: true })
        }
        catch {
            clearWatchdog()
            mobilePaySignupCookie.value = undefined

            const eventName = signup ? 'signup_mobilepay_error' : 'login_mobilepay_error'

            trackEvent({
                event_name: eventName,
                event_type: 'Click',
                meta_data: [
                    {
                        category: signup ? 'Signup' : 'Login',
                        message: errorMessage,
                    },
                ],
            })

            setPending(false)
            setAlert({
                type: 'warning',
                message: errorMessage,
            })
        }
    }

    // Where to send the user once the exchange resolves: back to where they started the flow, or
    // the frontpage. Consumed once.
    const resolveReturnPath = () => {
        const url = userInfo.returnUrlAfterLogin.value

        if (url && safeRedirect.isSafe(url)) {
            userInfo.returnUrlAfterLogin.value = undefined

            return url
        }

        return '/'
    }

    async function exchange(
        togglePending: (pending: boolean) => void,
        setAlert: (alert: { type: UIAlertTypeUnion, message: string, actions?: AuthAlertAction[] }) => void,
        query: Record<string, any>
    ) {
        togglePending(true)

        const signup = mobilePaySignupCookie.value === true

        // On failure, redirect back to the origin page (frontpage/lobby) with the auth dialog open
        // — the light callback page fetches none of the promotions/content the dialog needs.
        // Internal navigation (not external) so the useState alert survives and the page refetches.
        // Alert is set after navigating; the landing AuthContainer mounts fresh and reads it.
        const failExchange = async ({ message, errorCode, type = 'danger', actions, reason, outcome }: {
            message: string
            errorCode?: string
            type?: UIAlertTypeUnion
            actions?: AuthAlertAction[]
            reason?: string | null
            outcome?: 'cancelled'
        }) => {
            const eventName = signup ? 'signup_mobilepay_error' : 'login_mobilepay_error'

            trackEvent({
                event_name: eventName,
                event_type: 'Click',
                meta_data: [
                    {
                        category: signup ? 'Signup' : 'Login',
                        message,
                        ...(errorCode && { error_code: errorCode }),
                        ...(reason && { reason }),
                        ...(outcome && { outcome }),
                    },
                ],
            })

            mobilePaySignupCookie.value = undefined

            togglePending(false)

            await navigateTo({
                path: resolveReturnPath(),
                query: {
                    auth: signup ? 'signup' : 'login',
                },
            }, { replace: true })

            setAlert({ type, message, actions })
        }

        if (query.error) {
            mobilePayError({
                error: query.error as string,
                error_description: query.error_description as string,
            }).catch(() => {})

            if (CANCELLED_ERRORS.includes(query.error)) {
                // Both actions skip the bonus step: the signup dialog restores the pick (useSignupContainer).
                await failExchange({
                    message: t('core.auth.mobilepay.cancelled'),
                    errorCode: query.error,
                    type: 'info',
                    outcome: 'cancelled',
                    actions: signup
                        ? [
                                {
                                    label: t('core.auth.mobilepay.actions.retry'),
                                    onClick: () => links.signup.navigate({ query: { step: '2' } }),
                                },
                                {
                                    label: t('core.auth.mobilepay.actions.continueWithEmail'),
                                    onClick: () => links.signup.navigate({ query: { step: '3' } }),
                                },
                            ]
                        : undefined,
                })

                return
            }

            await failExchange({ message: genericExchangeErrorMessage, errorCode: query.error as string })

            return
        }

        try {
            const data = await exchangeMobilePay({
                code: query.code as string,
                state: query.state as string,
            })

            if (!data) {
                await failExchange({ message: genericExchangeErrorMessage })
                return
            }

            if (data.status !== 'logged_in') {
                const code = data.code ?? ''
                const reason = data.details?.reason
                const rejection = code === 'UA:2200' && reason ? signupRejections[reason] : undefined

                await failExchange({
                    message: rejection?.message ?? errorCodeMappings[code] ?? genericExchangeErrorMessage,
                    errorCode: code || undefined,
                    actions: rejection?.actions,
                    reason,
                })

                return
            }

            // The backend decides signup vs login: `created` is true for a new account, false when
            // the CPR already had one (the player is logged in instead) and null for a MobilePay login.
            if (data.created) {
                userInfo.userJustSignedUp.value = true

                // Consumers read `signup_completed` as "registration done", but only the
                // manual-form path fired it — MobilePay signups were undercounted (~22% of DK).
                await trackEvent({
                    event_name: versionedEventName('signup_completed', signupVersion.value),
                    event_type: 'Click',
                    meta_data: [{ category: 'Signup' }],
                })

                await trackEvent({
                    event_name: 'signup_mobilepay_completed',
                    event_type: 'Click',
                    meta_data: [{ category: 'Signup' }],
                })
            }
            else if (data.created === false) {
                userInfo.userJustLoggedIn.value = true

                await trackEvent({
                    event_name: 'signup_mobilepay_existing_user',
                    event_type: 'Click',
                    meta_data: [{ category: 'Signup' }],
                })
            }
            else {
                userInfo.userJustLoggedIn.value = true

                await trackEvent({
                    event_name: 'login_mobilepay_completed',
                    event_type: 'Click',
                    meta_data: [{ category: 'Login' }],
                })
            }

            await userInfo.refresh({ dedupe: 'cancel', cause: 'refresh:manual' })

            mobilePaySignupCookie.value = undefined
            promotionPick.value = undefined

            await navigateTo(data.created ? t('pages.deposit') : resolveReturnPath(), { external: true })
        }
        catch {
            await failExchange({ message: genericExchangeErrorMessage })
        }
    }

    return {
        login,
        exchange,
    }
}
